// Shared "add to calendar" section — used on BOTH /webinar-thank-you and
// /webinar-calendar so the two surfaces always render the identical cards and
// pull from the same central webinar constant (src/lib/webinar.js).
//
// Change the date once in webinar.js and every calendar link (Google, Apple,
// Outlook, .ics) on both pages updates together.

import React from 'react';
import { Icon } from './Icon.jsx';
import { trackCalendarAdd } from '../lib/tracking.js';
import {
  WEBINAR_DURATION_MINUTES,
  getGoogleCalendarUrl,
  getOutlook365CalendarUrl,
  downloadIcs,
} from '../lib/webinar.js';

// ─── CALENDAR ICONS (inline, brand-recognisable) ───────────────────────────
export const CalendarServiceIcon = {
  Google: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V9h14v11z" fill="#4285F4"/>
      <circle cx="9" cy="14" r="1.5" fill="#EA4335"/>
      <circle cx="15" cy="14" r="1.5" fill="#FBBC04"/>
      <circle cx="12" cy="17" r="1.5" fill="#34A853"/>
    </svg>
  ),
  Apple: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.5 12.6c0-2.8 2.3-4.1 2.4-4.2-1.3-1.9-3.3-2.2-4-2.2-1.7-.2-3.3 1-4.2 1-.9 0-2.2-1-3.7-1-1.9 0-3.7 1.1-4.7 2.8-2 3.5-.5 8.7 1.4 11.6.9 1.4 2 3 3.5 2.9 1.4-.1 1.9-.9 3.6-.9s2.2.9 3.7.9c1.5 0 2.5-1.4 3.4-2.8 1.1-1.6 1.5-3.2 1.5-3.3 0-.1-3-1.1-3-4.8zM14.7 4.4c.8-.9 1.3-2.2 1.1-3.4-1.1.1-2.4.7-3.2 1.6-.7.8-1.4 2.1-1.2 3.3 1.2.1 2.5-.6 3.3-1.5z"/>
    </svg>
  ),
  Outlook: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="6" width="20" height="13" rx="2" fill="#0078D4"/>
      <path d="M2 6h20l-10 7L2 6z" fill="#106EBE"/>
      <circle cx="8" cy="12.5" r="2.6" fill="#fff"/>
      <circle cx="8" cy="12.5" r="1.3" fill="#0078D4"/>
    </svg>
  ),
  Download: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
};

function CalendarCard({ icon, name, sub, href, onClick }) {
  const Component = href ? 'a' : 'button';
  // Always attach onClick — for the <a> variants it's the GA tracking
  // callback that fires alongside (not instead of) the navigation. The
  // browser still follows the href because we don't preventDefault.
  const extraProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer', onClick }
    : { type: 'button', onClick };
  return (
    <Component className="calendar-card" {...extraProps}>
      <span className="calendar-card-icon">{icon}</span>
      <span className="calendar-card-text">
        <span className="calendar-card-name">{name}</span>
        <span className="calendar-card-sub">{sub}</span>
      </span>
      <span className="calendar-card-arrow"><Icon.ArrowRight size={14} /></span>
    </Component>
  );
}

// The add-to-calendar section.
// - `source`    → passed to the GA trackCalendarAdd event so we can tell
//                 thank-you-page adds apart from standalone /webinar-calendar adds.
// - `showHead`  → render the section heading + lead (default true). The
//                 standalone /webinar-calendar page hides it so the buttons
//                 sit right at the top under its own compact intro.
// - `className` → extra class on the root <section> (e.g. spacing overrides).
// Just the four calendar-provider buttons — reusable on its own (e.g. inside
// the thank-you page's guided "step 2" card) without the section wrapper/head.
export function CalendarGrid({ source = 'thankyou' }) {
  return (
    <div className="calendar-grid">
      <CalendarCard
        icon={<CalendarServiceIcon.Google />}
        name="Google Calendar"
        sub="In neuem Tab öffnen"
        href={getGoogleCalendarUrl()}
        onClick={() => trackCalendarAdd({ provider: 'google', source })}
      />
      <CalendarCard
        icon={<CalendarServiceIcon.Apple />}
        name="Apple Calendar"
        sub="Lädt eine .ics-Datei"
        onClick={() => { trackCalendarAdd({ provider: 'apple_ics', source }); downloadIcs(); }}
      />
      <CalendarCard
        icon={<CalendarServiceIcon.Outlook />}
        name="Outlook"
        sub="office.com (Microsoft 365)"
        href={getOutlook365CalendarUrl()}
        onClick={() => trackCalendarAdd({ provider: 'outlook', source })}
      />
      <CalendarCard
        icon={<CalendarServiceIcon.Download />}
        name=".ics herunterladen"
        sub="Für alle anderen Kalender"
        onClick={() => { trackCalendarAdd({ provider: 'generic_ics', source }); downloadIcs(); }}
      />
    </div>
  );
}

export function CalendarSection({ source = 'thankyou', showHead = true, className = '' } = {}) {
  return (
    <section className={`section thankyou-calendar-section${className ? ` ${className}` : ''}`}>
      <div className="container">
        {showHead && (
          <div className="section-head">
            <span className="eyebrow">Termin sichern</span>
            <h2 className="section-title">
              Trag dir den Termin direkt in deinen <span className="serif-italic">Kalender</span> ein.
            </h2>
            <p className="lead">
              Damit du das Webinar auf keinen Fall verpasst. Wähle einfach deinen Kalender. Titel,
              Datum, Uhrzeit und {WEBINAR_DURATION_MINUTES} Minuten Dauer sind bereits vorausgefüllt.
            </p>
          </div>
        )}

        <CalendarGrid source={source} />
      </div>
    </section>
  );
}
